import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Form, Tag, Tooltip } from 'antd';
import { CommentOutlined, ExclamationCircleOutlined, LinkOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { SelectWithTextSelection } from '../../../common/components/SelectWithTextSelection';
import {
    WidgetDataItem,
    GroupedSelectGroupBehavior,
    InputBindingType,
    ExternalSystemValueMarker,
    ConstantValueMarker
} from '../../../common/services/types';
import { DynamicControlProps } from './types';
import { Utils } from '../../../common/misc/Utils';
import { ConfidenceLevel } from './ConfidenceLevel';

type GroupedOption = {
    label: string;
    value: string;
    rawValue: string;
    groupKey: string;
    groupLabel: string;
    groupIndex: number;
    optionIndex: number;
};

type SelectGroup = {
    label: string;
    key: string;
    groupIndex: number;
    options: { label: string; value: string }[];
};

type ParsedSelectValue = {
    groupKey: string;
    rawValue: string;
};

type SelectedTagDisplayValue = {
    groupLabel?: string;
    tagLabel: React.ReactNode;
};

const valueSeparator = '::';
const countryGroupLabelFallbacks: Record<string, string> = {
    WW: 'Worldwide'
};
const externalSystemMarkerFieldSuffix = '-external_system_markers';
const constantValueMarkerFieldSuffix = '-constant_value_markers';
const externalSystemTooltip = 'Retrieved from external system';
const emptyGroupedSelectMappings: GroupedSelectGroupBehavior[] = [];

export const DynamicGroupedSelect: React.FC<DynamicControlProps> = ({
    inputParams,
    ui,
    form,
    onHighlightBlock,
    getGearIcon,
    handleCommentFieldClick
}) => {
    const inputSelectedValues = getInitialSelectedValues(inputParams.value);
    const inputExternalSystemValueMarkers = getInitialExternalSystemValueMarkers(
        inputParams.meta?.externalSystemValueMarkers
    );
    const inputConstantValueMarkers = getInitialConstantValueMarkers(inputParams.meta?.constantValueMarkers);
    const inputValuesSignature = JSON.stringify({
        selectedValues: inputSelectedValues,
        externalSystemValueMarkers: inputExternalSystemValueMarkers,
        constantValueMarkers: inputConstantValueMarkers
    });
    const syncedInputValuesSignature = useRef(inputValuesSignature);
    const [allItems, setAllItems] = useState<WidgetDataItem[]>([]);
    const [selectedValues, setSelectedValues] = useState<string[]>(inputSelectedValues);
    const [externalSystemValueMarkers, setExternalSystemValueMarkers] = useState<ExternalSystemValueMarker[]>(
        inputExternalSystemValueMarkers
    );
    const [constantValueMarkers, setConstantValueMarkers] = useState<ConstantValueMarker[]>(
        inputConstantValueMarkers
    );
    const [loading, setLoading] = useState<boolean>(false);

    const groupConfig = inputParams.behavior?.groupedSelect;
    const mappings = groupConfig?.groups ?? emptyGroupedSelectMappings;
    const externalSystemMarkerField =
        inputParams.meta?.externalSystemMarkerField || `${inputParams.id}${externalSystemMarkerFieldSuffix}`;
    const constantValueMarkerField =
        inputParams.meta?.constantValueMarkerField || `${inputParams.id}${constantValueMarkerFieldSuffix}`;
    const requiredMappings = mappings.filter(mapping => mapping.required?.value === true);
    const directlyRequired = inputParams.validation?.required?.value === true;
    const isRequired = directlyRequired || requiredMappings.length > 0;
    const reviewWarningMessage = getReviewWarningMessage(inputParams.meta);
    const allowedExternalSystemValues = useMemo(() => getAllowedExternalSystemValues(inputParams.meta), [inputParams.meta]);

    useEffect(() => {
        let isMounted = true;
        setLoading(true);
        ui.getAllWidgetValues(inputParams.source.id, inputParams.parentInputId).then(r => {
            if (!isMounted) {
                return;
            }

            setAllItems(r.data);
            setLoading(false);
        });

        return () => {
            isMounted = false;
        };
    }, [inputParams.parentInputId, inputParams.source.id, ui]);

    const flatOptions = useMemo<GroupedOption[]>(() => {
        const groupIndexes = new Map<string, number>();

        return allItems
            .filter(item => getItemStringValue(item, 'groupKey') && getItemStringValue(item, 'groupLabel'))
            .map((item, optionIndex) => {
                const groupKey = getItemStringValue(item, 'groupKey');
                if (!groupIndexes.has(groupKey)) {
                    groupIndexes.set(groupKey, groupIndexes.size);
                }

                const rawValue = getRawValue(getItemStringValue(item, 'value'));

                return {
                    label: getItemLabel(item),
                    value: toSelectValue(groupKey, rawValue),
                    rawValue,
                    groupKey,
                    groupLabel: getItemStringValue(item, 'groupLabel'),
                    groupIndex: groupIndexes.get(groupKey)!,
                    optionIndex
                };
            })
            .filter(option => allowedExternalSystemValues.size === 0 || allowedExternalSystemValues.has(option.value));
    }, [allItems, allowedExternalSystemValues]);

    const optionMetaByValue = useMemo(
        () => new Map(flatOptions.map(option => [option.value, option])),
        [flatOptions]
    );

    const optionMetaByLookupValue = useMemo(() => {
        const lookup = new Map<string, GroupedOption>();
        flatOptions.forEach(option => {
            [
                option.value,
                option.rawValue,
                toSelectValue(option.groupKey, option.rawValue),
                `${option.groupKey}:${option.rawValue}`
            ].forEach(value => {
                lookup.set(normalizeComparableValue(value), option);
            });
        });

        return lookup;
    }, [flatOptions]);

    const selectOptionsKey =
        flatOptions.length === 0 ? 'grouped-select-loading' : `grouped-select-loaded-${inputParams.source.id}`;

    const groupedOptions = useMemo<SelectGroup[]>(() => {
        const groups = new Map<string, SelectGroup>();
        flatOptions.forEach(option => {
            if (!groups.has(option.groupKey)) {
                groups.set(option.groupKey, {
                    label: option.groupLabel,
                    key: option.groupKey,
                    groupIndex: option.groupIndex,
                    options: []
                });
            }

            groups.get(option.groupKey)!.options.push({ label: option.label, value: option.value });
        });

        return Array.from(groups.values()).sort(
            (a, b) =>
                getGroupSortOrder(mappings, a.key) - getGroupSortOrder(mappings, b.key) ||
                a.groupIndex - b.groupIndex
        );
    }, [flatOptions, mappings]);

    useEffect(() => {
        const sortedValues = sortByGroupOrder(normalizeSelectedValues(selectedValues));
        if (!areStringArraysEqual(sortedValues, selectedValues)) {
            setSelectedValues(sortedValues);
            updateMappedFields(sortedValues, externalSystemValueMarkers, constantValueMarkers);
            return;
        }

        updateMappedFields(selectedValues, externalSystemValueMarkers, constantValueMarkers);
        // Re-run when source metadata arrives so initial visible values populate hidden target fields.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [optionMetaByValue]);

    useEffect(() => {
        if (syncedInputValuesSignature.current === inputValuesSignature) {
            return;
        }

        syncedInputValuesSignature.current = inputValuesSignature;

        const sortedValues = sortByGroupOrder(normalizeSelectedValues(inputSelectedValues));
        setSelectedValues(sortedValues);
        setExternalSystemValueMarkers(inputExternalSystemValueMarkers);
        setConstantValueMarkers(inputConstantValueMarkers);
        updateMappedFields(sortedValues, inputExternalSystemValueMarkers, inputConstantValueMarkers);
        // Re-run only when the incoming backend value/marker payload changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputValuesSignature]);

    const sortByGroupOrder = (values: string[]) => {
        return [...values].sort((a, b) => {
            const optionA = getOptionByValue(a);
            const optionB = getOptionByValue(b);

            return (
                getGroupSortOrder(mappings, optionA?.groupKey || parseSelectValue(String(a))?.groupKey) -
                    getGroupSortOrder(mappings, optionB?.groupKey || parseSelectValue(String(b))?.groupKey) ||
                (optionA?.groupIndex ?? 999) - (optionB?.groupIndex ?? 999) ||
                (optionA?.optionIndex ?? 999) - (optionB?.optionIndex ?? 999)
            );
        });
    };

    const handleChange = (nextValues: string[]) => {
        const addedValue = nextValues.find(item => !selectedValues.includes(item));
        const normalizedValues = addedValue ? enforceMultiplicity(nextValues, addedValue) : nextValues;
        const sortedValues = sortByGroupOrder(normalizedValues);
        const nextExternalSystemValueMarkers = externalSystemValueMarkers.filter(marker => {
            const markerValue = normalizeMarkerValue(marker);
            return marker.value && selectedValues.includes(markerValue) && sortedValues.includes(markerValue);
        });
        const nextConstantValueMarkers = constantValueMarkers.filter(marker => {
            const markerValue = normalizeMarkerValue(marker);
            return marker.value && selectedValues.includes(markerValue) && sortedValues.includes(markerValue);
        });

        setSelectedValues(sortedValues);
        setExternalSystemValueMarkers(nextExternalSystemValueMarkers);
        setConstantValueMarkers(nextConstantValueMarkers);
        updateMappedFields(sortedValues, nextExternalSystemValueMarkers, nextConstantValueMarkers);
    };

    const enforceMultiplicity = (values: string[], addedValue: string) => {
        const addedOption = getOptionByValue(addedValue);
        const mapping = addedOption ? findGroupMapping(mappings, addedOption.groupKey) : undefined;

        if (!addedOption || mapping?.multiple !== false) {
            return values;
        }

        return values.filter(value => {
            const option = getOptionByValue(value);
            return option?.groupKey !== addedOption.groupKey || value === addedValue;
        });
    };

    const normalizeSelectedValues = (values: string[]) => {
        if (flatOptions.length === 0) {
            return values;
        }

        return values.map(value => getOptionByValue(value)?.value || value);
    };

    const getOptionByValue = (value: unknown) => {
        const stringValue = String(value);
        const parsedValue = parseSelectValue(stringValue);
        const rawValue = getRawValue(stringValue);

        return (
            optionMetaByValue.get(stringValue) ||
            optionMetaByLookupValue.get(normalizeComparableValue(stringValue)) ||
            (parsedValue
                ? optionMetaByLookupValue.get(
                      normalizeComparableValue(toSelectValue(parsedValue.groupKey, parsedValue.rawValue))
                  ) || optionMetaByLookupValue.get(normalizeComparableValue(`${parsedValue.groupKey}:${rawValue}`))
                : undefined) ||
            optionMetaByLookupValue.get(normalizeComparableValue(rawValue))
        );
    };

    const getTagDisplayValue = (value: unknown, fallbackLabel: React.ReactNode): SelectedTagDisplayValue => {
        const option = getOptionByValue(value);
        const parsedValue = parseSelectValue(String(value));
        const groupLabel =
            option?.groupLabel ||
            (parsedValue ? flatOptions.find(item => item.groupKey === parsedValue.groupKey)?.groupLabel : undefined);
        const rawValue = getRawValue(String(value));
        const tagLabel =
            getCountryGroupFallbackLabel(option || parsedValue, rawValue) ||
            option?.label ||
            getReadableFallbackLabel(fallbackLabel, rawValue);

        return { groupLabel, tagLabel };
    };

    const updateMappedFields = (
        values: string[],
        externalMarkers: ExternalSystemValueMarker[] = externalSystemValueMarkers,
        constantMarkers: ConstantValueMarker[] = constantValueMarkers
    ) => {
        const mappedValues = mappings.reduce((acc, mapping) => {
            acc[mapping.targetField] = mapping.multiple === false ? undefined : [];
            return acc;
        }, {} as Record<string, unknown>);
        const normalizedExternalMarkers = normalizeMarkers(externalMarkers);
        const normalizedConstantMarkers = normalizeMarkers(constantMarkers);

        values.forEach(value => {
            const option = getOptionByValue(value);
            if (!option) {
                return;
            }

            const mapping = findGroupMapping(mappings, option.groupKey);
            if (!mapping) {
                return;
            }

            const mappedValue = toMappedValue(option, mapping);
            if (mapping.multiple === false) {
                mappedValues[mapping.targetField] = mappedValue;
                return;
            }

            (mappedValues[mapping.targetField] as unknown[]).push(mappedValue);
        });

        form.setFieldsValue({
            [inputParams.id]: values,
            [externalSystemMarkerField]: normalizedExternalMarkers,
            [constantValueMarkerField]: normalizedConstantMarkers,
            ...mappedValues
        });
    };

    const hasExternalSystemMarker = (value: unknown) => {
        const normalizedValue = getOptionByValue(value)?.value || String(value);
        return externalSystemValueMarkers.some(marker => normalizeMarkerValue(marker) === normalizedValue);
    };

    const normalizeMarkerValue = <T extends { value: string }>(marker: T) => {
        return getOptionByValue(marker.value)?.value || marker.value;
    };

    const normalizeMarkers = <T extends { value: string }>(markers: T[]) => {
        return markers.map(marker => ({
            ...marker,
            value: normalizeMarkerValue(marker)
        }));
    };

    const hasSelectedValueForMapping = (values: string[], mapping: GroupedSelectGroupBehavior) => {
        return values.some(value => {
            const option = getOptionByValue(value);
            const groupKey = option?.groupKey || parseSelectValue(String(value))?.groupKey;

            return groupKey ? groupMappingMatchesGroup(mapping, groupKey) : false;
        });
    };

    const validateRequiredGroups = (_: unknown, values: string[] | undefined) => {
        const currentValues = Array.isArray(values) ? values : [];
        const missingRequiredMapping = requiredMappings.find(
            mapping => !hasSelectedValueForMapping(currentValues, mapping)
        );

        if (missingRequiredMapping) {
            return Promise.reject(
                new Error(getRequiredMappingMessage(missingRequiredMapping, inputParams.name))
            );
        }

        if (directlyRequired && currentValues.length === 0) {
            return Promise.reject(
                new Error(inputParams.validation?.required?.message || `${inputParams.name} is required`)
            );
        }

        return Promise.resolve();
    };

    const toMappedValue = (option: GroupedOption, mapping: GroupedSelectGroupBehavior) => {
        if (mapping.valueShape !== 'stateReference') {
            return option.rawValue;
        }

        const match = mapping.groupKeyRegex ? new RegExp(mapping.groupKeyRegex).exec(option.groupKey) : null;
        const namedGroups = (match as RegExpExecArray & { groups?: { isoAlpha3?: string } } | null)?.groups;
        return {
            code: option.rawValue,
            isoAlpha3: namedGroups?.isoAlpha3 || match?.[1] || ''
        };
    };

    const highlightField = () => {
        const fieldData = Utils.extractFieldData(inputParams);
        if (fieldData) {
            onHighlightBlock(fieldData, inputParams.id);
        }
    };

    const getTooltip = () => {
        if (!inputParams.behavior || !inputParams.behavior.inputTooltip) {
            return null;
        }

        return (
            <Tooltip overlayClassName="alpha-input-tooltip" title={inputParams.behavior.inputTooltip} placement="top">
                <QuestionCircleOutlined style={{ marginLeft: 6, verticalAlign: 'middle' }} />
            </Tooltip>
        );
    };

    const getGroupedConstantGearIcon = () => {
        const selectedConstantValueMarkers = getSelectedConstantValueMarkers();
        if (selectedConstantValueMarkers.length === 0) {
            return null;
        }

        return (
            <Tooltip title={getConstantValueMarkersTooltipTitle(selectedConstantValueMarkers)}>
                <i className="alpha-icon xs gear" />
            </Tooltip>
        );
    };

    const getSelectedConstantValueMarkers = () => {
        const selectedValueSet = new Set(selectedValues);
        return constantValueMarkers.filter(marker => marker.value && selectedValueSet.has(normalizeMarkerValue(marker)));
    };

    const getReviewWarningIcon = () => {
        if (!reviewWarningMessage) {
            return null;
        }

        return (
            <Tooltip title={reviewWarningMessage}>
                <ExclamationCircleOutlined
                    aria-label={reviewWarningMessage}
                    style={{ marginLeft: 6, color: '#d48806', verticalAlign: 'middle' }}
                />
            </Tooltip>
        );
    };

    const label = (
        <span className="alpha-doc-control-label with-controls">
            <Tooltip title={inputParams.name}>
                <span className="alpha-doc-control-name">{inputParams.name}</span>
            </Tooltip>
            <span className="controls-container">
                {inputParams.behavior?.readonly ? <></> : <ConfidenceLevel input={inputParams} />}
                {inputParams.behavior?.comments && (
                    <CommentOutlined
                        className="comments-icon"
                        onClick={() => handleCommentFieldClick!(inputParams.id)}
                    />
                )}
                {(inputParams.meta && inputParams.meta.field && inputParams.value != null && (
                    <Tooltip title="View in document">
                        <i className="alpha-icon xs form-anchor" onClick={highlightField} />
                    </Tooltip>
                )) ||
                    getGearIcon(inputParams)}
                {getGroupedConstantGearIcon()}
                {getReviewWarningIcon()}
                {getTooltip()}
            </span>
        </span>
    );

    return (
        <>
            {mappings.map(mapping => (
                <Form.Item key={mapping.targetField} name={mapping.targetField} noStyle>
                    <HiddenValueInput />
                </Form.Item>
            ))}
            <Form.Item name={externalSystemMarkerField} noStyle>
                <HiddenValueInput />
            </Form.Item>
            <Form.Item name={constantValueMarkerField} noStyle>
                <HiddenValueInput />
            </Form.Item>
            <Form.Item
                className={inputParams.behavior && inputParams.behavior.width === 1 ? 'wide' : ''}
                data-id={inputParams.id}
                data-type={inputParams.controlType}
                label={label}
                colon={false}
                labelCol={{ span: 24 }}
                name={inputParams.id}
                initialValue={selectedValues}
                required={isRequired}
                rules={[
                    {
                        validator: validateRequiredGroups
                    }
                ]}
            >
                <SelectWithTextSelection
                    key={selectOptionsKey}
                    allowClear={!isRequired}
                    disabled={(inputParams.behavior && inputParams.behavior.readonly) || loading}
                    loading={loading}
                    showSearch
                    optionFilterProp="label"
                    style={Utils.buildDynamicInputStyles(inputParams)}
                    mode="multiple"
                    placeholder=""
                    getPopupContainer={trigger => trigger.parentNode as HTMLElement}
                    options={groupedOptions}
                    value={selectedValues}
                    onChange={handleChange}
                    filterOption={(input, option) => {
                        const search = input.toLowerCase();
                        const meta = getOptionByValue(option?.value);
                        const labelValue = String(option?.label || '').toLowerCase();
                        const rawValue = String(meta?.rawValue || '').toLowerCase();
                        const groupLabel = String(meta?.groupLabel || '').toLowerCase();

                        return (
                            labelValue.includes(search) ||
                            rawValue.includes(search) ||
                            groupLabel.includes(search)
                        );
                    }}
                    tagRender={props => {
                        const { groupLabel, tagLabel } = getTagDisplayValue(props.value, props.label);
                        const showExternalSystemMarker = hasExternalSystemMarker(props.value);

                        return (
                            <Tag
                                closable={props.closable}
                                onClose={props.onClose}
                                style={{ marginRight: 4 }}
                            >
                                {groupLabel && (
                                    <>
                                        <span style={{ color: '#6f7b8a', fontWeight: 600 }}>
                                            {groupLabel}:
                                        </span>{' '}
                                    </>
                                )}
                                {tagLabel}
                                {showExternalSystemMarker && (
                                    <Tooltip title={externalSystemTooltip}>
                                        <LinkOutlined
                                            aria-label={externalSystemTooltip}
                                            style={{ marginLeft: 6, color: '#4b647f' }}
                                        />
                                    </Tooltip>
                                )}
                            </Tag>
                        );
                    }}
                    suffixIcon={<i className="alpha-icon xxs arrow-down-icon" style={{ margin: 0, width: 8 }} />}
                />
            </Form.Item>
        </>
    );
};

function toSelectValue(groupKey: string, rawValue: string) {
    return `${groupKey}${valueSeparator}${rawValue}`;
}

function getItemLabel(item: WidgetDataItem) {
    return getItemStringValue(item, 'text') || getItemStringValue(item, 'label') || getItemStringValue(item, 'name');
}

function getItemStringValue(item: WidgetDataItem, key: string) {
    const matchingKey = Object.keys(item).find(itemKey => itemKey.toLowerCase() === key.toLowerCase());
    return String((matchingKey ? item[matchingKey] : undefined) || '').trim();
}

function parseSelectValue(value: string): ParsedSelectValue | null {
    const separatorIndex = value.indexOf(valueSeparator);
    if (separatorIndex < 0) {
        return null;
    }

    return {
        groupKey: value.substring(0, separatorIndex),
        rawValue: value.substring(separatorIndex + valueSeparator.length)
    };
}

function getRawValue(value: string) {
    return (parseSelectValue(value)?.rawValue || value).trim();
}

function getReadableFallbackLabel(fallbackLabel: React.ReactNode, rawValue: string) {
    if (typeof fallbackLabel !== 'string') {
        return fallbackLabel || rawValue;
    }

    return parseSelectValue(fallbackLabel)?.rawValue || fallbackLabel || rawValue;
}

function getCountryGroupFallbackLabel(
    value: Pick<GroupedOption, 'groupKey'> | ParsedSelectValue | null | undefined,
    rawValue: string
) {
    if (normalizeComparableValue(value?.groupKey || '') !== 'country-group') {
        return undefined;
    }

    return countryGroupLabelFallbacks[rawValue.toUpperCase()];
}

function normalizeComparableValue(value: string) {
    return value.trim().toLowerCase();
}

function findGroupMapping(mappings: GroupedSelectGroupBehavior[], groupKey: string) {
    return mappings.find(mapping => groupMappingMatchesGroup(mapping, groupKey));
}

function getGroupSortOrder(mappings: GroupedSelectGroupBehavior[], groupKey: string | undefined) {
    if (!groupKey) {
        return 999;
    }

    return findGroupMapping(mappings, groupKey)?.sortOrder ?? 999;
}

function groupMappingMatchesGroup(mapping: GroupedSelectGroupBehavior, groupKey: string) {
    if (mapping.groupKey && mapping.groupKey === groupKey) {
        return true;
    }

    return mapping.groupKeyRegex ? new RegExp(mapping.groupKeyRegex).test(groupKey) : false;
}

function areStringArraysEqual(left: string[], right: string[]) {
    return left.length === right.length && left.every((value, index) => value === right[index]);
}

function getInitialSelectedValues(value: unknown) {
    return Array.isArray(value) ? value.map(item => String(item)) : [];
}

function getInitialExternalSystemValueMarkers(markers: ExternalSystemValueMarker[] | undefined) {
    return getInitialMarkers<ExternalSystemValueMarker>(markers, marker => ({
        sourceSystem: getMarkerStringValue(marker, 'sourceSystem'),
        businessId: getMarkerStringValue(marker, 'businessId'),
        date: getMarkerStringValue(marker, 'date')
    }));
}

function getInitialConstantValueMarkers(markers: ConstantValueMarker[] | undefined) {
    return getInitialMarkers<ConstantValueMarker>(markers);
}

function getInitialMarkers<T extends { value: string }>(
    markers: T[] | undefined,
    extraFields: (marker: unknown) => Partial<T> = () => ({})
) {
    if (!Array.isArray(markers)) {
        return [];
    }

    return markers
        .map(marker => ({
            ...extraFields(marker),
            value: getMarkerStringValue(marker, 'value'),
            rawValue: getMarkerStringValue(marker, 'rawValue'),
            groupKey: getMarkerStringValue(marker, 'groupKey'),
            targetField: getMarkerStringValue(marker, 'targetField')
        } as unknown as T))
        .filter(marker => marker.value.trim().length > 0);
}

function getMarkerStringValue(marker: unknown, key: string) {
    if (!marker || typeof marker !== 'object') {
        return '';
    }

    const matchingKey = Object.keys(marker).find(markerKey => markerKey.toLowerCase() === key.toLowerCase());
    const value = matchingKey ? (marker as Record<string, unknown>)[matchingKey] : undefined;
    return typeof value === 'string' ? value.trim() : '';
}

function getAllowedExternalSystemValues(meta: DynamicControlProps['inputParams']['meta']) {
    const allowedValues = new Set<string>();
    const sourceValues = meta?.externalSystem?.sourceValues;
    if (!sourceValues || typeof sourceValues !== 'object') {
        return allowedValues;
    }

    Object.values(sourceValues).forEach(value => {
        if (!Array.isArray(value)) {
            return;
        }

        value.forEach(item => {
            if (typeof item === 'string' && item.trim()) {
                allowedValues.add(item.trim());
            }
        });
    });

    return allowedValues;
}

function getReviewWarningMessage(meta: DynamicControlProps['inputParams']['meta']) {
    const message = meta?.reviewWarning?.message;
    return typeof message === 'string' ? message.trim() : '';
}

function getRequiredMappingMessage(mapping: GroupedSelectGroupBehavior, inputName: string) {
    const message = mapping.required?.message?.trim();
    if (message && message !== 'This field is required.') {
        return message;
    }

    return `${toReadableFieldName(mapping.targetField) || inputName} is required.`;
}

function getConstantValueMarkersTooltipTitle(markers: ConstantValueMarker[]) {
    const fieldNames = Array.from(new Set(markers.map(marker => marker.targetField)))
        .map(fieldId => toReadableFieldName(fieldId || ''))
        .filter(Boolean);
    return `${InputBindingType.Constant}s: ${fieldNames.join(', ')}`;
}

function toReadableFieldName(fieldId: string) {
    return fieldId
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_\-.]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

const HiddenValueInput: React.FC = () => null;

export default DynamicGroupedSelect;
